import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

const hasToken = Boolean(import.meta.env.VITE_NEW_RELIC_LICENSE_KEY);

export interface NewRelicBrowser {
    setCustomAttribute(name: string, value: string | number | boolean | null): void;
    setUserId(userId: string | null): void;
    noticeError(error: Error, customAttributes?: Record<string, string | number | boolean>): void;
    setCurrentRouteName(name: string): void;
    addRelease(name: string, version: string): void;
}

function logMock(method: string, ...args: unknown[]): void {
    // oxlint-disable-next-line no-console
    console.info(`[NewRelic mock] ${method}`, ...args);
}

const devMock: NewRelicBrowser = {
    setCustomAttribute: (name, value) => logMock('setCustomAttribute', name, value),
    setUserId: (userId) => logMock('setUserId', userId),
    noticeError: (error, attrs) => logMock('noticeError', error.message, attrs ?? ''),
    setCurrentRouteName: (name) => logMock('setCurrentRouteName', name),
    addRelease: (name, version) => logMock('addRelease', name, version),
};

function createAgent(): NewRelicBrowser {
    const agent = new BrowserAgent({
        init: {
            distributed_tracing: {
                // Automatically injects newrelic + W3C traceparent/tracestate headers into every
                // same-origin fetch/XHR call. The backend accepts these, linking browser sessions to backend transactions.
                enabled: true,
                cors_use_newrelic_header: false,
                cors_use_tracecontext_headers: false,
            },
            // cookies_enabled: true is the default and is intentionally kept.
            // It is required for session tracking, session trace, and session replay features.
            ajax: {
                // Exclude NR's own data collection endpoint from AJAX monitoring.
                deny_list: ['bam.eu01.nr-data.net'],
            },
        },
        info: {
            beacon: 'bam.eu01.nr-data.net',
            errorBeacon: 'bam.eu01.nr-data.net',
            licenseKey: import.meta.env.VITE_NEW_RELIC_LICENSE_KEY as string,
            applicationID: import.meta.env.VITE_NEW_RELIC_APP_ID as string,
            sa: 1,
        },
        loader_config: {
            accountID: import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID as string,
            trustKey: import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID as string,
            agentID: import.meta.env.VITE_NEW_RELIC_APP_ID as string,
            licenseKey: import.meta.env.VITE_NEW_RELIC_LICENSE_KEY as string,
            applicationID: import.meta.env.VITE_NEW_RELIC_APP_ID as string,
        },
    });

    return {
        setCustomAttribute: (name, value) => {
            agent.setCustomAttribute(name, value);
        },
        setUserId: (userId) => {
            agent.setUserId(userId);
        },
        noticeError: (error, attrs) => {
            agent.noticeError(error, attrs);
        },
        setCurrentRouteName: (name) => {
            agent.setCurrentRouteName(name);
        },
        addRelease: (name, version) => {
            agent.addRelease(name, version);
        },
    };
}

export const newrelicBrowser: NewRelicBrowser = hasToken ? createAgent() : devMock;
